import { motion } from "framer-motion";
import UtilBar from "@/components/UtilBar";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import QuoteModal from "@/components/QuoteModal";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import usePageSEO from "@/hooks/usePageSEO";
import PageFAQ from "@/components/PageFAQ";
import { alternaInstallFAQs } from "@/data/faqData";
import RelatedServices from "@/components/RelatedServices";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";
import { Link } from "react-router-dom";
import { Home, Droplets, Ruler, Layers } from "lucide-react";
import heroImg from "@/assets/alterna-tile-kitchen-hero.webp";

const fade = { initial: { opacity: 0, y: 28 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const AlternaInstallation = () => {
  usePageSEO({
    title: "Luxury Vinyl Tile Installation | Armstrong Alterna Dealer",
    description: "Authorized Armstrong Alterna luxury vinyl tile installation. Engineered tile with flush-mount Schluter trim transitions. NWFA-certified installer.",
    path: "/alterna-tile-installation",
    // Service schema baked into static HTML via generate-seo-html.mjs
  });

  return (
    <div className="min-h-screen">
      <UtilBar />
      <SiteHeader />
      <main>
        {/* ══ HERO ══ */}
        <section className="relative min-h-[560px] max-h-[800px] h-[75svh] flex items-end overflow-hidden bg-foreground">
          <PageBreadcrumb variant="overlay" items={[
            { label: "Home", to: "/" },
            { label: "Services" },
            { label: "Alterna Installation" },
          ]} />
          <div className="absolute inset-0">
            <img width={1920} height={1080} loading="eager" fetchPriority="high" decoding="async"
              src={heroImg}
              alt="Armstrong Alterna engineered tile flush transition to hardwood flooring in Michigan MI 1 Wood Floors"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/95 via-foreground/50 to-foreground/20" />

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 pb-10 md:pb-16 px-5 md:px-10 lg:px-16 max-w-4xl"
          >
            <p className="text-[10px] md:text-[11px] font-semibold tracking-[.14em] uppercase text-primary mb-3 md:mb-4 flex items-center gap-2">
              <span className="w-7 h-px bg-primary inline-block" />
              Alterna Engineered Tile Installation Michigan
            </p>
            <h1 className="font-display text-[clamp(28px,5.5vw,58px)] font-semibold leading-[1.08] tracking-tight text-primary-foreground">
              Alterna Engineered Tile Installation
            </h1>
            <p className="font-display text-[clamp(18px,3vw,30px)] font-light text-primary-foreground/70 mt-2 leading-[1.2]">
              Flush Transitions to Hardwood — No Trip Hazards, Just Architectural Flow
            </p>
            <p className="mt-4 md:mt-6 text-[13px] md:text-[15px] font-light text-primary-foreground/60 max-w-lg leading-relaxed">
              The warmer, smarter stone alternative. Engineered to flow seamlessly into your hardwood with zero trip hazards and zero compromise.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-6 md:mt-8">
              <QuoteModal
                trigger={
                  <button className="bg-primary text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:bg-[hsl(var(--gold-400))] transition-colors text-center">
                    Request a Forensic Site Evaluation →
                  </button>
                }
              />
              <a
                href="tel:2483258853"
                className="border border-primary-foreground/25 text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:border-primary-foreground/60 transition-colors text-center"
              >
                Call (248) 325-8853
              </a>
            </div>
          </motion.div>
        </section>

        {/* ══ THE TOTAL SOLUTION ══ */}
        <section className="bg-background py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">One Company, Every Room</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-foreground mb-6">
                The Total Solution
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 md:gap-12">
              <motion.div {...fade} transition={{ duration: 0.6, delay: 0.1 }} className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Home className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-foreground mb-2">Why We Offer Alterna</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Armstrong Alterna is our exclusive luxury vinyl tile (LVT) — a stone-composite engineered tile that outperforms ceramic in warmth, flexibility and repairability, without the cold feel of traditional stone.
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed mt-3">
                      We are hardwood specialists first. But our clients should never have to hire a separate tile contractor for their kitchen or bathroom. Armstrong Alterna gives us a high-performance solution for every wet area in your home. One company, one aesthetic vision, one standard of care from the front door to the primary bath.
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div {...fade} transition={{ duration: 0.6, delay: 0.2 }} className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Droplets className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-foreground mb-2">Why Alterna Over Ceramic</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Alterna is warmer on the feet than ceramic or porcelain. It can be grouted for a traditional stone look or installed without grout for easy maintenance. Individual tiles can be replaced without demolishing the entire floor. And with 48 designs spanning marble, travertine, slate, and concrete aesthetics, we can create custom mixed-color patterns that rival any natural stone installation.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ SEAMLESS FLOW ══ */}
        <section className="bg-foreground py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Flush Transition Engineering</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-primary-foreground mb-6">
                Seamless Flow Between Worlds
              </h2>
              <div className="max-w-3xl space-y-4">
                <p className="text-sm text-primary-foreground/70 leading-relaxed">
                  The hardest part of any tile-to-hardwood transition is not the materials. It is the math. Alterna tiles and hardwood planks have different thicknesses, different underlayment requirements, and different subfloor demands. If the installer does not engineer the subfloor heights in advance, you end up with a visible step between rooms, a trip hazard, and a visual break that cheapens both materials.
                </p>
                <p className="text-sm text-primary-foreground/70 leading-relaxed">
                  We solve this at the subfloor level. Before a single tile or plank is laid, we calculate the finished height of each material and build up or grind down the substrate to achieve a perfectly flush plane. The Alterna tile in your kitchen sits at exactly the same height as the white oak in your dining room. The transition between them is a clean, deliberate line, not an accident of uneven installation. This is the engineering behind{" "}
                  <Link to="/installation" className="text-primary hover:text-[hsl(var(--gold-300))] underline underline-offset-2 transition-colors">
                    seamless hardwood integration
                  </Link>.
                </p>
                <div className="flex items-center gap-3 pt-2">
                  <Ruler className="w-5 h-5 text-primary flex-shrink-0" />
                  <p className="text-xs font-semibold tracking-wide uppercase text-primary">
                    Subfloor heights engineered to 1/16 inch precision
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ══ SCHLUTER TRIMS ══ */}
        <section className="bg-background py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Transition Architecture</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-foreground mb-6">
                Schluter Precision Trims
              </h2>
              <div className="max-w-3xl space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Where Alterna meets hardwood, we use Schluter metal trims to create a clean, modern, and trip-free transition. Schluter profiles are the industry standard in high-end tile work, used in luxury hotels and commercial spaces worldwide. We bring that same standard to residential Michigan homes.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Available in brushed stainless, polished chrome, and anodized aluminum, Schluter trims provide a permanent, corrosion-resistant edge that protects the tile perimeter while creating a deliberate architectural boundary between flooring types. No plastic reducers. No silicone caulk. Just precision metalwork that lasts as long as the floor itself.
                </p>
              </div>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 mt-10">
              <motion.div {...fade} transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-muted/50 border border-border rounded-2xl p-6 md:p-8"
              >
                <Layers className="w-8 h-8 text-primary mb-4" />
                <h3 className="font-display text-lg font-semibold text-foreground mb-3">Schluter Reno-T</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  The Reno-T profile creates a smooth, flush transition between tile and adjoining hardwood at the same height. The T-shaped design anchors beneath the tile while providing a clean metal cap at the surface. No exposed edges, no lip to catch toes.
                </p>
              </motion.div>

              <motion.div {...fade} transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-muted/50 border border-border rounded-2xl p-6 md:p-8"
              >
                <Layers className="w-8 h-8 text-primary mb-4" />
                <h3 className="font-display text-lg font-semibold text-foreground mb-3">TOP Step Flush Mount Vents</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  In Alterna installations, our flush-mount vents are custom-matched to the tile pattern and color. They sit level with the tile surface, maintaining the seamless stone aesthetic throughout the room without the visual disruption of raised metal registers.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ CTA ══ */}
        <section className="bg-primary py-10 md:py-14">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-[clamp(24px,3vw,36px)] font-semibold text-primary-foreground leading-[1.1] mb-2">
                Stone aesthetics. <em className="italic font-normal text-[hsl(var(--gold-300))]">Hardwood precision.</em>
              </h3>
              <p className="text-[13px] font-light text-primary-foreground/65 leading-relaxed">
                One company for every room. From white oak great rooms to Alterna kitchens, we engineer every transition to architectural perfection.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0 w-full md:w-auto">
              <QuoteModal
                trigger={
                  <button className="bg-primary-foreground text-primary px-7 py-3.5 rounded-xl text-xs font-bold tracking-[.05em] uppercase hover:-translate-y-px transition-transform whitespace-nowrap text-center">
                    Schedule a Forensic Site Evaluation →
                  </button>
                }
              />
              <a
                href="tel:2483258853"
                className="border border-primary-foreground/30 text-primary-foreground px-7 py-3.5 rounded-xl text-xs font-semibold tracking-[.05em] hover:border-primary-foreground hover:text-primary-foreground transition-colors whitespace-nowrap text-center"
              >
                📞 (248) 325-8853
              </a>
            </div>
          </div>
        </section>

        <RelatedServices
          heading="Every flush transition starts beneath the surface."
          subheading="Foundation & Product Selection"
          links={[
            { title: "Subfloor Leveling & Repair", desc: "Flush transitions demand perfect subfloor heights. Laser-guided flatness mapping, self-leveling compounds, and mechanical grinding to engineer seamless material connections.", to: "/subfloor-leveling", tag: "Foundation" },
            { title: "Armstrong Alterna Collections", desc: "Browse all 48 Alterna engineered tile designs spanning marble, travertine, slate, and concrete aesthetics with 100% waterproof construction.", to: "/brands/alterna", tag: "Product Selection" },
          ]}
        />

        <GoogleReviewsCarousel contextTags={["installation"]} count={4} />
        <PageFAQ suppressJsonLd faqs={alternaInstallFAQs} heading={<>Alterna installation questions <em className="italic font-normal text-primary">answered.</em></>} subheading="Alterna Installation FAQs" />
      </main>
      <SiteFooter />
    </div>
  );
};

export default AlternaInstallation;
